import { useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

type NumberInputProps = {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  decimals?: number;
  className?: string;
};

function formatDisplay(n: number, decimals: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

function parseInput(raw: string): number {
  // Accept both comma and dot as decimal separator, strip thousands separators
  const normalized = raw.replace(/\s/g, '').replace(',', '.');
  return parseFloat(normalized);
}

export function NumberInput({
  id,
  value,
  onChange,
  min,
  max,
  decimals = 0,
  className,
}: NumberInputProps) {
  const [display, setDisplay] = useState(() => formatDisplay(value, decimals));
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);

  const errorId = id ? `${id}-error` : undefined;

  // Sync when external value changes while not focused (e.g. store update from another source)
  useEffect(() => {
    if (!focused.current) {
      setDisplay(formatDisplay(value, decimals));
    }
  }, [value, decimals]);

  const handleFocus = () => {
    focused.current = true;
    setError(null);
    // Show raw value for easy editing
    setDisplay(String(value).replace('.', ','));
  };

  const handleBlur = () => {
    focused.current = false;
    const parsed = parseInput(display);

    if (isNaN(parsed)) {
      setError('Valeur invalide');
      setDisplay(formatDisplay(value, decimals));
      return;
    }

    if (min !== undefined && parsed < min) {
      setError('Valeur minimale : ' + formatDisplay(min, decimals));
      setDisplay(formatDisplay(value, decimals));
      return;
    }

    if (max !== undefined && parsed > max) {
      setError('Valeur maximale : ' + formatDisplay(max, decimals));
      setDisplay(formatDisplay(value, decimals));
      return;
    }

    setError(null);
    onChange(parsed);
    setDisplay(formatDisplay(parsed, decimals));
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        id={id}
        inputMode="decimal"
        value={display}
        aria-invalid={error !== null}
        aria-describedby={error && errorId ? errorId : undefined}
        className={cn('text-right tabular-nums', className)}
        onChange={(e) => setDisplay(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
